import { Block, BlockSchemaFromSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { AbsoluteCenter, Box, Button, HStack, ProgressCircle, Text } from '@chakra-ui/react';
import { PageContext } from 'components/common/PageContext';
import {
  useFinalSubmitWorksheet,
  useGetWorksheetSession,
  useSaveWorksheetAnswers,
} from 'hooks/api/backend/worksheets/useWorksheets';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import 'styles/worksheets/styles.css';
import 'styles/worksheets/sessionStyles.css';
import { createEditorWithMode } from 'utils/createEditorWithMode';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { useIsScrolled } from 'hooks/utils/useIsScrolled';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { WorksheetSessionProvider } from 'contexts/WorksheetSessionContext';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { logBrazeEvent } from 'utils/brazeEvents';
import { CustomBlockContentSpec, schema } from './Worksheet';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export const WorksheetSession = (): ReactElement => {
  const toast = useNovaToast();
  const isTablet = useBreakpoint(Breakpoints.TABLET);

  const scrollThresholdInPx = 150;
  const isScrolled = useIsScrolled(scrollThresholdInPx);

  const [initialContent, setInitialContent] = useState<CustomBlockContentSpec[] | null>(null);
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get('viewType') || '';
  const isWebview = viewType === 'webview';
  const { id } = useParams<{ id: string; webview: string }>();
  const { data: worksheetData, isLoading, isError, error, refetch } = useGetWorksheetSession(id);
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { mutateAsync: saveWorksheetAnswers } = useSaveWorksheetAnswers();
  const { mutateAsync: finalSubmitWorksheet, isPending: isFinalSubmitting } =
    useFinalSubmitWorksheet();

  // Tracks which worksheet id we've already fired 'worksheet_started' for, so
  // refetches/re-renders within the same session don't duplicate the event.
  const trackedWorksheetStartIdRef = useRef<string | null>(null);

  const editor = useMemo(() => {
    if (initialContent === null || initialContent.length === 0)
      return createEditorWithMode('student', { schema });
    return createEditorWithMode('student', { schema, initialContent });
  }, [initialContent]);

  // Load initial content from the worksheet data or local storage to editor
  useEffect(() => {
    const loadContent = async (): Promise<void> => {
      if (!worksheetData) return;
      if (worksheetData.status === 'SUBMITTED') setIsSubmitted(true);

      const worksheetContent = worksheetData.content;
      if (worksheetContent && worksheetContent.length > 0) {
        setInitialContent(worksheetContent);
        return;
      }

      setInitialContent([]);
    };

    loadContent();
  }, [worksheetData, id]);

  // Fire 'worksheet_started' the first time a worksheet with no existing
  // answer is opened (mirrors the previous backend check on !hasAnswer).
  //
  // In the native app this page is loaded inside a WebView (viewType=webview).
  // Rather than relying on the web Braze SDK inside that WebView - whose init
  // timing and user identity aren't guaranteed to match the native session -
  // delegate the actual logCustomEvent call to the native app's own Braze SDK
  // via postMessage. Outside the WebView (plain web usage) track it here directly.
  useEffect(() => {
    if (!worksheetData) return;
    if (worksheetData.status === null && trackedWorksheetStartIdRef.current !== worksheetData.id) {
      trackedWorksheetStartIdRef.current = worksheetData.id;

      if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'WORKSHEET_STARTED', worksheetId: worksheetData.id })
        );
      } else {
        logBrazeEvent('worksheet_started', { worksheetId: worksheetData.id });
      }
    }
  }, [worksheetData, isWebview]);

  const handleBack = (): void => {
    navigate(-1);
    // TODO: Add unsaved changes warning
    // TODO: Add confirmation dialog
  };

  const getProgressFromDocument = (
    document: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]
  ): { completed: number; total: number; percent: number } => {
    const completed = document.filter((block) => {
      if (
        block.type === 'multipleChoice' ||
        block.type === 'selfAssessment' ||
        block.type === 'textQuestion'
      ) {
        return block.props.isSubmitted;
      }

      return false;
    }).length;

    const total = document.filter(
      (block) =>
        block.type === 'multipleChoice' ||
        block.type === 'selfAssessment' ||
        block.type === 'textQuestion'
    ).length;

    return {
      completed,
      total,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  };

  const progressInPercent = useMemo(() => {
    return getProgressFromDocument(editor.document).percent;
  }, [editor.document]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const isInteractiveAnswerMissing = editor.document.some((block) => {
      if (block.type === 'multipleChoice') {
        const isNotAnswered = !block.props.isSubmitted;
        return isNotAnswered;
      }
      if (block.type === 'selfAssessment') {
        const isNotAnswered = !block.props.isSubmitted;
        return isNotAnswered;
      }
      if (block.type === 'textQuestion') {
        const isNotAnswered = !block.props.isSubmitted;
        return isNotAnswered;
      }
      return false;
    });
    if (isInteractiveAnswerMissing) {
      const firstUnansweredBlock = editor.document.find((block) => {
        if (
          block.type === 'multipleChoice' ||
          block.type === 'selfAssessment' ||
          block.type === 'textQuestion'
        ) {
          const isNotAnswered = !block.props.isSubmitted;
          return isNotAnswered;
        }
        return false;
      });
      if (firstUnansweredBlock) {
        const blockElement = document.querySelector(`[data-id="${firstUnansweredBlock.id}"]`);
        if (blockElement) {
          blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }
    if (!worksheetData) return;

    try {
      const result = await finalSubmitWorksheet({
        content: editor.document as Block[],
        id: worksheetData.id,
        progressInPercent,
      });
      // Only set isSubmitted state after successful final submit
      if (result.success && result.status === 'SUBMITTED') {
        setIsSubmitted(true);

        // Notify React Native WebView after final submit is done. As with
        // 'worksheet_started', let the native app log 'worksheet_completed' via
        // its own Braze SDK instead of the web SDK inside the WebView.
        if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'ANSWERS_SUBMITTED', worksheetId: worksheetData.id })
          );
        } else {
          logBrazeEvent('worksheet_completed', { worksheetId: worksheetData.id });
          toast.show({
            title: 'Deine Antworten wurden erfolgreich abgeschickt.',
            variant: ToastVariant.SUCCESS,
          });
        }
      }
    } catch (error) {
      // Handle error case
      if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'ANSWERS_SUBMISSION_FAILED' })
        );
      } else {
        toast.show({
          title: 'Fehler beim Abschicken der Antworten',
          variant: ToastVariant.ERROR,
        });
      }
    }
  }, [editor, isWebview, finalSubmitWorksheet, progressInPercent, toast, worksheetData]);

  useEffect(() => {
    if (!editor || !isWebview) return;

    const handleMessage = async (event: Event): Promise<void> => {
      // Cast event to MessageEvent to access .data
      const messageEvent = event as MessageEvent;
      try {
        const data = JSON.parse(messageEvent.data);
        if (data.type === 'SUBMIT_ANSWERS') {
          handleSubmit();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Invalid message received', e);
      }
    };

    // Use a wrapper to ensure correct event type for document
    const documentMessageListener = (event: Event): void => {
      handleMessage(event);
    };

    document.addEventListener('message', documentMessageListener as EventListener);
    window.addEventListener('message', handleMessage as EventListener);

    return () => {
      window.removeEventListener('message', handleMessage as EventListener);
      document.removeEventListener('message', documentMessageListener as EventListener);
    };
  }, [editor, isWebview, handleSubmit, toast]);

  useEffect(() => {
    if (
      isWebview &&
      window.ReactNativeWebView &&
      window.ReactNativeWebView.postMessage &&
      worksheetData?.status === 'SUBMITTED'
    ) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'DISABLE_SUBMIT' }));
    }
  }, [isWebview, worksheetData]);

  useEffect(() => {
    if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'PROGRESS_UPDATE',
          progress: progressInPercent,
        })
      );
    }
  }, [isWebview, progressInPercent]);

  useEffect(() => {
    if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'TITLE_UPDATE',
          title: worksheetData?.title,
        })
      );
    }
  }, [isWebview, worksheetData?.title]);

  useEffect(() => {
    if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'SCROLL_DEPTH_UPDATE',
          isScrolled,
        })
      );
    }
  }, [isWebview, isScrolled]);

  const ButtonComponents = (
    <HStack gap="l">
      <HStack gap="10px">
        {isTablet && (
          <>
            <Text textStyle="body.m.medium" color="text.secondary">
              Fortschritt
            </Text>
            <ProgressCircle.Root value={progressInPercent} size="xl">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range stroke="icon.accent" strokeLinecap="round" />
              </ProgressCircle.Circle>
              <AbsoluteCenter>
                <ProgressCircle.ValueText />
              </AbsoluteCenter>
            </ProgressCircle.Root>
          </>
        )}
      </HStack>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isSubmitted}
        loading={isFinalSubmitting}
      >
        {isSubmitted ? 'Abgeschickt' : 'Absenden'}
      </Button>
    </HStack>
  );

  const onBlockSave = async (
    document: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]
  ): Promise<void> => {
    if (!worksheetData) return;
    const { percent } = getProgressFromDocument(document);
    await saveWorksheetAnswers({
      content: document,
      id: worksheetData.id,
      progressInPercent: percent,
    });
  };

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <PageContext
      title={worksheetData?.title}
      hasBackButton
      customOnBackClick={handleBack}
      worksheetSessionOptions={{
        ButtonComponents,
        title: worksheetData?.title || '',
      }}
    >
      <Box
        bg="background.primary"
        p={{ base: 'l', md: '40px' }}
        width="100%"
        borderRadius={{ md: '15px' }}
        mt={{ base: '40px', md: '90px' }}
      >
        {isWebview && (
          <Text textStyle="h3.medium" fontWeight="semibold" mb="15px">
            {worksheetData?.title}
          </Text>
        )}
        {editor && (
          <WorksheetSessionProvider onSave={onBlockSave}>
            <BlockNoteView
              slashMenu={false}
              editable={false}
              editor={editor}
              style={{
                width: '100%',
                overflow: 'auto',
              }}
              data-theming-css-variables
              // Apply native styling for webview
              {...(isWebview ? { 'data-theming-native': true } : {})}
            />
          </WorksheetSessionProvider>
        )}
      </Box>
    </PageContext>
  );
};
